import { noopTelemetry } from '../chatTelemetry';
import { isTurnTerminal, unwrapTurnMessages, mapBreezeRpcError } from '../utils/chatHelpers';
const INITIAL_POLL_DELAY = 500;
const BASE_POLL_INTERVAL = 1000;
const MAX_POLL_INTERVAL = 4000;
const MAX_CONSECUTIVE_ERRORS = 5;
const TERMINAL_ERROR_TYPES = new Set(['NOT_FOUND', 'UNAUTHORIZED']);
export function createPollingTransport(connection, logger, telemetry = noopTelemetry) {
  let afterMessageId;
  let abortController = null;
  let pollTimer = null;
  let activeConversationId = null;
  let activeTurnId = null;
  function stopPolling() {
    var _abortController;
    if (pollTimer !== null) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
    (_abortController = abortController) === null || _abortController === void 0 || _abortController.abort();
    abortController = null;
  }
  function schedulePoll(delay, handler) {
    stopPolling();
    pollTimer = setTimeout(() => {
      void pollForMessages(BASE_POLL_INTERVAL, 0, handler);
    }, delay);
  }
  async function pollForMessages(interval, consecutiveErrors, handler) {
    var _abortController2;
    logger.transportPollCycle(interval, consecutiveErrors);
    (_abortController2 = abortController) === null || _abortController2 === void 0 || _abortController2.abort();
    // eslint-disable-next-line compat/compat
    abortController = new AbortController();
    try {
      const result = await connection.getTurnResult(Object.assign({
        conversationId: activeConversationId,
        turnId: activeTurnId
      }, afterMessageId != null ? {
        afterMessageId
      } : {}));
      const newMessages = unwrapTurnMessages(result.messages);
      if (newMessages.length > 0) {
        handler.onMessageUpdate({
          type: 'replace-all',
          messages: newMessages
        });
      }
      if (result.lastMessageId != null) {
        afterMessageId = result.lastMessageId;
      }
      await handleTurnStatus(result.status, handler);
    } catch (error) {
      const chatError = mapBreezeRpcError(error);
      logger.transportError(chatError);
      telemetry.streamError(chatError);
      if (typeof chatError.type === 'string' && TERMINAL_ERROR_TYPES.has(chatError.type)) {
        handler.onError(chatError);
        return;
      }
      const nextErrors = consecutiveErrors + 1;
      if (nextErrors >= MAX_CONSECUTIVE_ERRORS) {
        handler.onError(chatError);
        return;
      }
      const nextInterval = Math.min(interval * 2, MAX_POLL_INTERVAL);
      stopPolling();
      pollTimer = setTimeout(() => {
        void pollForMessages(nextInterval, nextErrors, handler);
      }, nextInterval);
    }
  }
  async function handleTurnStatus(status, handler) {
    switch (status) {
      case 'PENDING':
      case 'IN_PROGRESS':
        {
          schedulePoll(BASE_POLL_INTERVAL, handler);
          return;
        }
      case 'COMPLETED':
        {
          handler.onComplete();
          return;
        }
      case 'FAILED':
        {
          handler.onError({
            type: 'UNKNOWN',
            message: 'Turn failed'
          });
          return;
        }
      case 'CANCELLED':
        {
          handler.onComplete();
          return;
        }
      case 'AWAITING_TOOL_RESULT':
        {
          await handler.onStopReason('AWAITING_TOOL_RESULT');
          return;
        }
      default:
        {
          if (isTurnTerminal(status)) {
            handler.onComplete();
            return;
          }
          schedulePoll(BASE_POLL_INTERVAL, handler);
          return;
        }
    }
  }
  const transport = {
    activeStatus: 'STREAMING',
    async send(request, handler) {
      try {
        var _request$externalSkil;
        // request.message is a union (string | content blocks) -- pass it
        // through unchanged. The connection picks the right CHIRP RPC.
        const response = await connection.sendMessage(Object.assign({
          agentId: request.agentId
        }, request.conversationId != null ? {
          conversationId: request.conversationId
        } : {}, request.collectionId ? {
          collectionId: request.collectionId
        } : {}, {
          message: request.message
        }, request.turnMetadata ? {
          turnMetadata: request.turnMetadata
        } : {}, request.conversationMetadata ? {
          conversationMetadata: request.conversationMetadata
        } : {}, request.messageContexts ? {
          messageContexts: request.messageContexts
        } : {}, request.fileReferences ? {
          fileReferences: request.fileReferences
        } : {}, request.configVersion != null ? {
          configVersion: request.configVersion
        } : {}, {
          externalToolSpecs: request.externalToolSpecs,
          externalSkillSpecs: (_request$externalSkil = request.externalSkillSpecs) !== null && _request$externalSkil !== void 0 ? _request$externalSkil : []
        }, request.externalSubagentSpecs ? {
          externalSubagentSpecs: request.externalSubagentSpecs
        } : {}));
        activeConversationId = response.conversationId;
        activeTurnId = response.turnId;
        afterMessageId = undefined;
        handler.onTurnStarted(response.conversationId, response.turnId);
        schedulePoll(INITIAL_POLL_DELAY, handler);
      } catch (error) {
        handler.onError(mapBreezeRpcError(error));
      }
    },
    async sendToolResponse(request, handler) {
      logger.transportToolResponse(request);
      await doSendToolResponse(request, handler, false);
    },
    async sendToolApproval(request, handler) {
      try {
        const response = await connection.sendToolApproval(Object.assign({
          conversationId: request.conversationId,
          turnId: request.turnId,
          approvals: request.approvals
        }, request.turnMetadata ? {
          turnMetadata: request.turnMetadata
        } : {}, request.messageContexts ? {
          messageContexts: request.messageContexts
        } : {}));
        activeTurnId = response.turnId;
        schedulePoll(INITIAL_POLL_DELAY, handler);
      } catch (error) {
        handler.onError(mapBreezeRpcError(error));
      }
    },
    resume(request, handler) {
      logger.transportResume(request);
      activeConversationId = request.conversationId;
      activeTurnId = request.turnId;
      afterMessageId = request.lastMessageId != null ? request.lastMessageId : undefined;
      schedulePoll(INITIAL_POLL_DELAY, handler);
      return Promise.resolve();
    },
    async cancelTurn(request) {
      logger.transportCancel(request);
      try {
        await connection.cancelTurn(Object.assign({
          conversationId: request.conversationId,
          turnId: request.turnId
        }, request.reason ? {
          reason: request.reason
        } : {}));
      } catch (_unused) {
        // best-effort
      }
    },
    stop() {
      stopPolling();
    },
    clear() {
      stopPolling();
      afterMessageId = undefined;
      activeConversationId = null;
      activeTurnId = null;
    },
    async submitFeedback(request) {
      try {
        await connection.submitFeedback(request);
      } catch (_unused2) {
        // fire-and-forget
      }
    }
  };
  async function doSendToolResponse(request, handler, isRetry) {
    try {
      var _request$externalSkil2;
      const response = await connection.sendToolResponse(Object.assign({
        conversationId: request.conversationId,
        turnId: request.turnId,
        toolResults: request.toolResults,
        externalToolSpecs: request.externalToolSpecs,
        externalSkillSpecs: (_request$externalSkil2 = request.externalSkillSpecs) !== null && _request$externalSkil2 !== void 0 ? _request$externalSkil2 : []
      }, request.externalSubagentSpecs ? {
        externalSubagentSpecs: request.externalSubagentSpecs
      } : {}, request.turnMetadata ? {
        turnMetadata: request.turnMetadata
      } : {}, request.messageContexts ? {
        messageContexts: request.messageContexts
      } : {}));
      activeTurnId = response.turnId;
      afterMessageId = undefined;
      schedulePoll(INITIAL_POLL_DELAY, handler);
    } catch (error) {
      const chatError = mapBreezeRpcError(error);
      if (!isRetry && (chatError.type === 'TRANSPORT_ERROR' || chatError.type === 'UNKNOWN')) {
        await new Promise(r => setTimeout(r, 1000));
        if (abortController === null) return;
        await doSendToolResponse(request, handler, true);
        return;
      }
      handler.onError(chatError);
    }
  }
  return transport;
}